// carrer's page 
import React from "react";
import { Link } from "react-router-dom";
import Navbar from "../../Components/common/Navbar";
import { useTranslation } from "react-i18next";

import {
  ArrowRight,
  Award,
  BookOpen,
  BriefcaseBusiness,
  CheckCircle2,
  Globe2,
  GraduationCap,
  HeartHandshake,
  Laptop2,
  Lightbulb,
  Rocket,
  ShieldCheck,
  Sparkles,
  Target,
  TrendingUp,
  Users,
} from "lucide-react";

export default function CareersPage() {
  const { t } = useTranslation();

  const reasons = [
    {
      icon: Globe2,
      title: "Inclusive & Global",
      description:
        "Be part of a learning ecosystem that welcomes ideas, experiences and perspectives from people across different backgrounds and communities.",
    },
    {
      icon: Lightbulb,
      title: "Innovation First",
      description:
        "Work in an environment that encourages new ideas, digital innovation, experimentation and continuous improvement.",
    },
    {
      icon: Users,
      title: "People Centric",
      description:
        "We believe strong organizations are built by empowered people. Collaboration, respect, learning and professional growth are central to our culture.",
    },
    {
      icon: Rocket,
      title: "Create Meaningful Impact",
      description:
        "Contribute to initiatives that make learning more accessible and help learners develop knowledge, skills and professional capabilities.",
    },
  ];

  const benefits = [
    {
      icon: TrendingUp,
      title: "Build Your Career",
      description:
        "Develop your professional journey through meaningful responsibilities, new challenges and opportunities to grow.",
    },
    {
      icon: HeartHandshake,
      title: "Open Work Culture",
      description:
        "Share ideas, collaborate with teams and contribute to an environment where creativity and constructive thinking are encouraged.",
    },
    {
      icon: GraduationCap,
      title: "Continuous Learning",
      description:
        "Stay connected with emerging knowledge, professional learning and skill-development opportunities.",
    },
    {
      icon: Sparkles,
      title: "Purpose-Driven Work",
      description:
        "Work on projects and initiatives that support education, innovation, professional development and lifelong learning.",
    },
  ];

  const culture = [
    "Collaboration",
    "Continuous Learning",
    "Innovation",
    "Professional Excellence",
    "Respect & Inclusion",
    "Learner Impact",
  ];

  const careerAreas = [
    {
      icon: Laptop2,
      title: "Technology & Digital",
      description:
        "Contribute to digital platforms, technology solutions, product development and emerging learning technologies.",
    },
    {
      icon: BookOpen,
      title: "Learning & Content",
      description:
        "Support course development, learning resources, instructional content and professional education initiatives.",
    },
    {
      icon: BriefcaseBusiness,
      title: "Operations & Partnerships",
      description:
        "Work with institutions, partners, trainers and internal teams to strengthen learning operations and collaboration.",
    },
    {
      icon: Target,
      title: "Marketing & Growth",
      description:
        "Help expand WiLearn's global learning community through communication, outreach, engagement and growth initiatives.",
    },
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar page="careers" />

      {/* HERO */}
      <section className="relative overflow-hidden bg-gradient-to-br from-violet-950 via-purple-900 to-fuchsia-800">
        <div className="absolute inset-0">
          <div className="absolute -left-28 top-10 h-80 w-80 rounded-full bg-pink-500/20 blur-3xl" />
          <div className="absolute right-0 top-0 h-96 w-96 rounded-full bg-violet-400/20 blur-3xl" />
          <div className="absolute bottom-0 left-1/2 h-72 w-72 rounded-full bg-fuchsia-400/15 blur-3xl" />
        </div>

        <div className="relative mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8 lg:py-20">
          <div className="mx-auto max-w-6xl text-center">
            <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-2 text-sm font-semibold text-violet-100 backdrop-blur-md">
              <BriefcaseBusiness className="h-4 w-4" />
              {t("Careers at WiLearn")}
            </div>

            <h1 className="mt-5 text-4xl font-black leading-tight text-white sm:text-5xl ">
              {t("Build Your Career. Create Global Impact.")}
            </h1>

            <p className="mt-4 text-lg font-bold uppercase tracking-[0.18em] text-pink-200">
              {t("LEARN. GROW. CONTRIBUTE.")}
            </p>

            <p className="mx-auto mt-6 max-w-5xl text-justify text-lg leading-8 text-violet-100 sm:text-xl">
              {t(
                "At WiLearn, we believe meaningful careers are built through continuous learning, collaboration, innovation and purpose. We are creating a global digital learning ecosystem that connects knowledge, professional development and technology."
              )}
            </p>

            {/* <p className="mx-auto mt-4 max-w-5xl text-justify text-base leading-8 text-violet-100 sm:text-lg">
              {t(
                "Join us and contribute to an environment where ideas are valued, people are encouraged to grow, and every role can help create meaningful learning opportunities for communities around the world."
              )}
            </p> */}

            <div className="mt-6 flex flex-wrap justify-center gap-4">
              <a
                href="#opportunities"
                className="inline-flex items-center gap-2 rounded-full bg-white px-7 py-3 font-bold text-violet-800 shadow-lg transition hover:-translate-y-1 hover:bg-violet-50"
              >
                {t("Explore Opportunities")}
                <ArrowRight className="h-4 w-4" />
              </a>

              <a
                href="#culture"
                className="rounded-full border border-white/30 bg-white/10 px-7 py-3 font-semibold text-white backdrop-blur-md transition hover:bg-white/20"
              >
                {t("Life at WiLearn")}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* WHY WILEARN */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-4xl text-center">
          <p className="text-sm font-bold uppercase tracking-[0.25em] text-violet-600">
            {t("WHY WILEARN")}
          </p>

          <h2 className="mt-3 text-3xl font-bold text-slate-900 sm:text-4xl">
            {t("Reasons to become part of WiLearn")}
          </h2>

          <p className="mx-auto mt-5 max-w-3xl text-justify text-lg leading-8 text-slate-600">
            {t(
              "We aim to build a professional environment where talented individuals can collaborate, learn continuously and contribute to meaningful digital education initiatives."
            )}
          </p>
        </div>

        <div className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
          {reasons.map((item) => {
            const Icon = item.icon;

            return (
              <div
                key={item.title}
                className="group rounded-3xl border border-slate-200 bg-white p-6 shadow-sm transition duration-300 hover:-translate-y-2 hover:border-pink-200 hover:shadow-xl"
              >
                <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-violet-100 to-pink-100 text-violet-700 transition group-hover:scale-110">
                  <Icon className="h-7 w-7" />
                </div>

                <h3 className="mt-5 text-xl font-bold text-slate-900">
                  {t(item.title)}
                </h3>

                <p className="mt-3 text-justify leading-7 text-slate-600">
                  {t(item.description)}
                </p>
              </div>
            );
          })}
        </div>
      </section>

      {/* WHAT'S IN IT FOR YOU */}
      <section className="bg-white py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-12 lg:grid-cols-[0.85fr_1.15fr] lg:items-center">
            <div>
              <p className="text-sm font-bold uppercase tracking-[0.25em] text-pink-600">
                {t("WHAT'S IN IT FOR YOU?")}
              </p>

              <h2 className="mt-3 text-3xl font-bold leading-tight text-slate-900 sm:text-4xl">
                {t("Grow personally and professionally")}
              </h2>

              <p className="mt-6 text-justify text-lg leading-8 text-slate-600">
                {t(
                  "A career should provide more than a job title. At WiLearn, we aim to create opportunities where people can strengthen their capabilities, explore new ideas and contribute to meaningful work."
                )}
              </p>

              <div className="mt-7 rounded-3xl bg-gradient-to-br from-violet-700 via-purple-700 to-pink-600 p-7 text-white shadow-xl">
                <Award className="h-10 w-10 text-pink-200" />

                <h3 className="mt-5 text-2xl font-bold">
                  {t("Your growth matters")}
                </h3>

                <p className="mt-3 text-justify leading-7 text-violet-100">
                  {t(
                    "We encourage continuous learning, professional development, collaboration and ownership so that individuals can grow alongside the organization."
                  )}
                </p>
              </div>
            </div>

            <div className="grid gap-5 sm:grid-cols-2">
              {benefits.map((benefit) => {
                const Icon = benefit.icon;

                return (
                  <div
                    key={benefit.title}
                    className="rounded-3xl border border-violet-100 bg-slate-50 p-6 transition duration-300 hover:-translate-y-1 hover:bg-white hover:shadow-lg"
                  >
                    <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-violet-100 text-violet-700">
                      <Icon className="h-6 w-6" />
                    </div>

                    <h3 className="mt-5 text-xl font-bold text-slate-900">
                      {t(benefit.title)}
                    </h3>

                    <p className="mt-3 text-justify leading-7 text-slate-600">
                      {t(benefit.description)}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* LIFE AT WILEARN */}
      <section
        id="culture"
        className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8"
      >
        <div className="overflow-hidden rounded-[2rem] border border-violet-100 bg-gradient-to-br from-white via-violet-50 to-pink-50 p-8 sm:p-10 lg:p-12">
          <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
            <div>
              <p className="text-sm font-bold uppercase tracking-[0.25em] text-violet-600">
                {t("LIFE AT WILEARN")}
              </p>

              <h2 className="mt-3 text-3xl font-bold leading-tight text-slate-900 sm:text-4xl">
                {t("A culture built around learning and collaboration")}
              </h2>

              <p className="mt-6 text-justify text-lg leading-8 text-slate-600">
                {t(
                  "WiLearn brings together education, technology, professional development and global collaboration. We encourage people to contribute ideas, solve problems together and continuously develop their knowledge and capabilities."
                )}
              </p>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              {culture.map((item) => (
                <div
                  key={item}
                  className="flex items-center gap-3 rounded-2xl border border-white bg-white p-4 shadow-sm"
                >
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-violet-100 to-pink-100">
                    <CheckCircle2 className="h-5 w-5 text-violet-700" />
                  </div>

                  <span className="font-semibold text-slate-800">
                    {t(item)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CAREER AREAS */}
      <section id="opportunities" className="bg-slate-950 py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-4xl text-center">
            <p className="text-sm font-bold uppercase tracking-[0.25em] text-pink-300">
              {t("CAREER OPPORTUNITIES")}
            </p>

            <h2 className="mt-3 text-3xl font-bold text-white sm:text-4xl">
              {t("Explore career areas at WiLearn")}
            </h2>

            <p className="mx-auto mt-5 max-w-3xl text-justify text-lg leading-8 text-slate-300">
              {t(
                "Career opportunities may be available across different areas based on organizational requirements, current projects and future growth."
              )}
            </p>
          </div>

          <div className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
            {careerAreas.map((area) => {
              const Icon = area.icon;

              return (
                <div
                  key={area.title}
                  className="group rounded-3xl border border-white/10 bg-white/5 p-6 backdrop-blur transition duration-300 hover:-translate-y-1 hover:border-pink-400/40 hover:bg-white/10"
                >
                  <div className="flex h-13 w-13 items-center justify-center rounded-2xl bg-violet-500/20 p-3 text-pink-300">
                    <Icon className="h-6 w-6" />
                  </div>

                  <h3 className="mt-5 text-xl font-bold text-white">
                    {t(area.title)}
                  </h3>

                  <p className="mt-3 text-justify leading-7 text-slate-300">
                    {t(area.description)}
                  </p>
                </div>
              );
            })}
          </div>

          <div className="mt-10 text-center">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-violet-600 to-pink-500 px-7 py-3 font-bold text-white shadow-lg transition hover:-translate-y-1"
            >
              {t("Contact Us About Opportunities")}
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* EQUAL OPPORTUNITY */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="grid gap-6 lg:grid-cols-2">
          <div className="rounded-3xl border border-violet-100 bg-white p-7 shadow-sm sm:p-8">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-violet-100 text-violet-700">
              <Users className="h-7 w-7" />
            </div>

            <h2 className="mt-5 text-2xl font-bold text-slate-900">
              {t("Equal Opportunity")}
            </h2>

            <p className="mt-4 text-justify leading-8 text-slate-600">
              {t(
                "WiLearn is committed to providing a respectful and inclusive professional environment. Employment and collaboration opportunities are considered based on relevant qualifications, skills, experience, performance and organizational requirements."
              )}
            </p>
          </div>

          <div className="rounded-3xl border border-pink-100 bg-pink-50/60 p-7 sm:p-8">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-pink-100 text-pink-600">
              <ShieldCheck className="h-7 w-7" />
            </div>

            <h2 className="mt-5 text-2xl font-bold text-slate-900">
              {t("Recruitment Safety")}
            </h2>

            <p className="mt-4 text-justify leading-8 text-slate-600">
              {t(
                "Applicants should remain cautious of fraudulent recruitment messages. WiLearn applicants should verify recruitment communication through official WiLearn channels before sharing sensitive information or responding to employment-related requests."
              )}
            </p>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="mx-auto max-w-7xl px-4 pb-16 sm:px-6 lg:px-8">
        <div className="relative overflow-hidden rounded-[2rem] bg-gradient-to-r from-violet-700 via-purple-700 to-pink-600 px-6 py-12 text-center shadow-2xl sm:px-10 lg:py-14">
          <div className="absolute -right-20 -top-20 h-72 w-72 rounded-full bg-white/10 blur-3xl" />
          <div className="absolute -bottom-20 -left-16 h-64 w-64 rounded-full bg-pink-200/10 blur-3xl" />

          <div className="relative">
            <Rocket className="mx-auto h-12 w-12 text-pink-200" />

            <p className="mt-5 text-sm font-bold uppercase tracking-[0.3em] text-pink-200">
              {t("JOIN OUR JOURNEY")}
            </p>

            <h2 className="mx-auto mt-3 max-w-4xl text-3xl font-black text-white sm:text-4xl">
              {t("Learn. Grow. Contribute. Create Impact.")}
            </h2>

            <p className="mx-auto mt-5 max-w-3xl text-lg leading-8 text-violet-100">
              {t(
                "Interested in contributing to WiLearn? Connect with us and explore opportunities to become part of our growing learning ecosystem."
              )}
            </p>

            <Link
              to="/contact"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-white px-7 py-3 font-bold text-violet-700 shadow-lg transition hover:-translate-y-1 hover:bg-violet-50"
            >
              {t("Connect With WiLearn")}
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}